import { ILocation } from '../interfaces/location.interface';
import { useRouter } from 'next/router';
import { CategoryFragment } from '../@types/codegen/graphql';
import {
  getServerPageLocationByUriComponent,
  getServerPagePlaces,
  getServerPageLocationByPlaceId,
  useCategoryByUriComponent,
} from '../@types/codegen/page';
import Bugsnag from '@bugsnag/js';
import { isBrowser } from './environment';
import { useCategoriesContext } from '../store/categories.context';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

export interface IQueryParam {
  [key: string]: string | null;
}

export interface IDigandoUrlInformations {
  uriComponent: string;
  // Stores the resolved location name from Google places-api. Do not use for redirects. Use origLocationName instead.
  locationName?: string;
  // Stores the original location name from the url. Use this location for redirects.
  origLocationName?: string;
  // Only use this value for static page generation. Use the cookie instead
  countryCode?: string;
  location?: ILocation;
  productName?: string;
  // We need to original category for the search
  originalCategory?: CategoryFragment | null;
  // The current category which is needed to display virtual and original categories
  currentCategory?: CategoryFragment | null;
  query?: IQueryParam;
}

interface IExtractResult {
  url: string;
  value: string | null;
}

export enum UrlKeys {
  LOCATION_NAME = 'l',
  COUNTRY_CODE = 'c',
}

/**
 * Search for location information in url
 *
 * Example:
 * param.url: /baumaschinen-mieten/bagger/l/Dornbirn
 *
 * This will return two values
 * res.value: Dornbirn - The information about the chosen location
 * res.url: /baumaschinen-mieten/bagger - The whole path for the category tree without any other informations
 */
const extractLocationName = (url: string): IExtractResult => {
  let regExpString = `\\/${UrlKeys.LOCATION_NAME}\\/([a-zA-ZäöüÄÖÜß_\\-0-9, ]{2,})`; // whitespace is necessary for e.g. "Zell am See"

  const locationPath = url.split('/l/')[1];

  // Check if the location in the url contains a state and a city
  if (locationPath && 1 < locationPath.split('/').length) {
    regExpString = `\\/${UrlKeys.LOCATION_NAME}\\/([a-zA-ZäöüÄÖÜß_\\-0-9, ]{2,})/([a-zA-ZäöüÄÖÜß_\\-0-9, ]{2,})`;
  }

  const regExp = new RegExp(regExpString);
  const results = url.match(regExp);

  if (null === results) {
    return {
      url,
      value: null,
    };
  }

  const locationName = results[0].replace('/l/', '');

  return {
    url: url.replace(results[0], '').toLowerCase(),
    value: locationName ?? null,
  };
};

const extractCountryCodeFromUrlParams = (categoryParams: string[] = []): { countryCode: string } => {
  if (false === Array.isArray(categoryParams)) {
    return {
      countryCode: '',
    };
  }

  const indexOfParam = categoryParams.findIndex((value) => value === UrlKeys.COUNTRY_CODE);
  let countryCode = '';

  if (-1 !== indexOfParam) {
    countryCode = categoryParams[indexOfParam + 1];
  }

  return {
    countryCode,
  };
};

export const extractCountryCode = (url: string): IExtractResult => {
  const regExpString = `\\/${UrlKeys.COUNTRY_CODE}\\/([A-Z]{2})`; // whitespace is necessary for e.g. "Zell am See"

  const regExp = new RegExp(regExpString);
  const results = url.match(regExp);

  if (null === results) {
    return {
      url,
      value: null,
    };
  }

  const countryCode = results[1];

  return {
    url: url.replace(results[0], ''),
    value: countryCode ?? null,
  };
};

/**
 * Load place information with the named lcoation from the url and return a location with lat and lng from a google result.
 */
const getLocationInformation = async (city: string): Promise<ILocation | null> => {
  const locationByUriComponentRes = await getServerPageLocationByUriComponent({
    variables: { uriComponent: city },
    fetchPolicy: 'cache-first',
  });
  const place = locationByUriComponentRes.props.data?.Geolocation_getLocationInformation;

  if (place) {
    return {
      name: place.name ?? '',
      lng: place.lng ?? 0,
      lat: place.lat ?? 0,
      radius: place.radius,
    };
  }

  const result: ILocation | null = await new Promise((resolve, reject) => {
    getServerPagePlaces({
      variables: {
        params: { text: city },
      },
    })
      .then(placesRes => {
        const places = placesRes.props.data?.Geolocation_places.data ?? [];

        if (0 === places.length) {
          reject();
        }

        resolve(places[0]);
      })
      .catch(reject);
  })
    .then(async (place) => {
      if (!place) {
        throw Error('Place not found');
      }

      const placeRes = await getServerPageLocationByPlaceId({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        variables: { id: place.id },
      });

      return placeRes.props.data?.Geolocation_locationByPlace ?? null;
    })
    .then((place) => {
      if (!place) {
        throw Error('Place not found');
      }

      return {
        lng: place.lng,
        lat: place.lat,
        name: place.name,
        radius: publicRuntimeConfig.geolocation.defaultRadius,
      };
    })
    .catch((error) => {
      Bugsnag.notify(`Place resolver "${city}" not found: ${JSON.stringify(error)}`);

      return null;
    }) as ILocation | null;

  return result;
};

/**
 * Returns information about the current search-page.
 * This will do the same as `useUrlInformation` but the function can be called in a server-side function.
 */
export const urlParser = async (url: string): Promise<Omit<IDigandoUrlInformations, 'originalCategory'>> => {
  const urlInformation: IDigandoUrlInformations = {
    uriComponent: '',
  };
  const locationName: IExtractResult = extractLocationName(url);
  const countryCode = extractCountryCode(locationName.url);

  if (null !== locationName.value) {
    const place = await getLocationInformation(`${locationName.value}`);

    urlInformation.origLocationName = locationName.value;

    if (null !== place) {
      // @deprecated Use location.name instead locationName
      urlInformation.locationName = place.name as string ?? '';
      urlInformation.location = place;
    }
  }

  if (null !== countryCode.value) {
    urlInformation.countryCode = countryCode.value;
  }

  urlInformation.uriComponent = countryCode.url.split('/').pop() ?? '';

  return urlInformation;
};

/**
 * Returns information about the current search-page.
 * This function is a custom React-Hook and can only be used in React-Components.
 */
export const useUrlInformation = (): Pick<IDigandoUrlInformations, 'uriComponent' | 'originalCategory' | 'countryCode' | 'query' | 'currentCategory'> => {
  const router = useRouter();
  const { categories, rootCategory } = useCategoriesContext();

  const url = new URL(router.asPath, 'https://www.digando.com');

  let countryCode = '';

  if (isBrowser()) {
    countryCode = extractCountryCodeFromUrlParams(router.query?.categories as string[] ?? []).countryCode;
  } else {
    countryCode = extractCountryCode(router.asPath).value || '';
  }

  const locationName: IExtractResult = extractLocationName(url.pathname.replace(`/c/${countryCode}`, ''));
  let uriComponent: string = locationName.url.split('/').pop() ?? '';

  uriComponent = 'baumaschinen-mieten' === uriComponent ? '' : uriComponent;

  const categoryRes = useCategoryByUriComponent(() => {
    return {
      fetchPolicy: 'cache-only',
      variables: {
        uriComponent: uriComponent,
      },
    };
  });

  const categoryByUriComponent = categoryRes.data?.categoryByUriComponent;

  let categoryMatch = categories.find(category => category.uriComponent === uriComponent) ?? null;
  let currentCategory: CategoryFragment | null = null;

  if (!categoryMatch) {

    // Check for virtual categories
    if (categoryByUriComponent) {
      categoryMatch = categories.find(category => category.uriComponent === categoryByUriComponent.originalUriComponent) ?? null;

      if (categoryMatch) {
        currentCategory = categoryByUriComponent;
      }
    }
  }

  if (!categoryMatch) {
    categoryMatch = rootCategory;
  }

  return {
    originalCategory: categoryMatch,
    currentCategory: currentCategory ?? categoryMatch,
    uriComponent: categoryMatch?.uriComponent ?? '',
    countryCode,
    query: router.query as IQueryParam,
  };
};
